import React from 'react';
import classNames from 'classnames';
import { LinkedLogo, NamedLink } from '../../../../components';
import { FormattedMessage } from 'react-intl';
import { useConfiguration } from '../../../../context/configurationContext';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import FooterLinkedLogo from '../../../../extensions/LandingPage/components/Logo/FooterLinkedLogo';
import SignUpForm from '../../../../extensions/LandingPage/components/SignUpForm/SignUpForm';

import css from './SectionFooter.module.css';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

const insertStoreLinkInFooterMarkdown = (content, storeUrl) => {
  if (!content || !storeUrl) {
    return content;
  }

  // Avoid duplicate insertion if Store already exists in the footer markdown.
  if (/(\[Store\]\(|>Store<)/i.test(content)) {
    return content;
  }

  const searchListingsLinkRegex = /(\[[^\]]*search\s+listings[^\]]*\]\([^\)]+\))/i;
  const aboutAndSearchInlineRegex = /about\s+us/i;

  // If both links are on the same markdown line, force visual line breaks.
  const inlineLineIndex = content
    .split('\n')
    .findIndex(line => aboutAndSearchInlineRegex.test(line) && searchListingsLinkRegex.test(line));

  if (inlineLineIndex !== -1) {
    const lines = content.split('\n');
    lines[inlineLineIndex] = lines[inlineLineIndex].replace(
      searchListingsLinkRegex,
      `<br />\n[Store](${storeUrl})\n<br />\n$1`
    );
    return lines.join('\n');
  }

  const lines = content.split('\n');
  const aboutUsIndex = lines.findIndex(line => /about\s+us/i.test(line));
  const searchListingsIndex = lines.findIndex(
    (line, index) => index > aboutUsIndex && /search\s+listings/i.test(line)
  );

  if (aboutUsIndex === -1 || searchListingsIndex === -1) {
    return content;
  }

  lines.splice(searchListingsIndex, 0, `[Store](${storeUrl})`);
  return lines.join('\n');
};

const injectStoreLinkInBlocks = (blocks, storeUrl) => {
  if (!storeUrl || !Array.isArray(blocks) || blocks.length === 0) {
    return blocks;
  }

  return blocks.map(block => {
    const isFooterMarkdownBlock =
      block?.blockType === 'footerBlock' &&
      block?.text?.fieldType === 'markdown' &&
      typeof block?.text?.content === 'string';

    if (!isFooterMarkdownBlock) {
      return block;
    }

    const nextContent = insertStoreLinkInFooterMarkdown(block.text.content, storeUrl);
    return nextContent !== block.text.content
      ? {
          ...block,
          text: {
            ...block.text,
            content: nextContent,
          },
        }
      : block;
  });
};

/**
 * @typedef {Object} SocialMediaLinkConfig
 * @property {'socialMediaLink'} fieldType
 * @property {string} platform
 * @property {string} url
 */

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.sectionId id of the section
 * @param {'footer'} props.sectionType
 * @param {number} props.numberOfColumns columns for blocks in footer (1-4)
 * @param {Array<SocialMediaLinkConfig>?} props.socialMediaLinks array of social media link configs
 * @param {Object?} props.slogan
 * @param {Object?} props.copyright
 * @param {Object?} props.appearance
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns = 1,
    socialMediaLinks = [],
    slogan,
    appearance,
    copyright,
    blocks = [],
    options,
    linkLogoToExternalSite,
  } = props;

  const config = useConfiguration();
  const blocksWithStoreLink = injectStoreLinkInBlocks(blocks, config.shopifyStoreUrl);

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';

  // use block builder instead of mapping blocks manually

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <div className={classNames(css.content, getContentCss(numberOfColumns))}>
          <div>
            <FooterLinkedLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoWrapper}
              logoImageClassName={css.logoImage}
              linkToExternalSite={linkLogoToExternalSite}
              layout={logoLayout}
            />
          </div>
          <div className={css.sloganMobile}>
            <Field data={slogan} className={css.slogan} />
          </div>
          <div className={css.detailsInfo}>
            <div className={css.sloganDesktop}>
              <Field data={slogan} className={css.slogan} />
            </div>
            <div className={css.signUpForm}>
              <p className={css.message}>
                <FormattedMessage id="SignUpForm.message" />
              </p>
              <SignUpForm />
            </div>
            {showSocialMediaLinks ? (
              <div className={css.icons}>
                <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
              </div>
            ) : null}
            <Field data={copyright} className={css.copyright} />
          </div>
          <div className={classNames(css.grid, getGridCss(numberOfColumns))}>
            <BlockBuilder blocks={blocksWithStoreLink} sectionId={sectionId} options={options} />
            <NamedLink name="TermsOfServicePage" className={css.customLinks}>
              <FormattedMessage id="Footer.terms" />
            </NamedLink>
            <NamedLink name="PrivacyPolicyPage" className={css.customLinks}>
              <FormattedMessage id="Footer.privacyPolicy" />
            </NamedLink>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionFooter;
